import { z } from "zod";
import { entryTypeSchema, visibilityLevelSchema } from "./enums.js";

export const handleSchema = z
  .string()
  .min(3)
  .max(30)
  .regex(/^[a-z0-9_]+$/, "lowercase letters, numbers and underscores only");

export const hhmmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM 24h");

export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");

function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
export const ianaTzSchema = z.string().refine(isValidTimezone, "invalid IANA timezone");

export const profileUpdateSchema = z
  .object({
    name: z.string().min(1).max(120),
    bio: z.string().max(2000),
    pronouns: z.string().max(40),
    city: z.string().max(120),
    country: z.string().max(120),
    country_code: z.string().length(2),
    timezone: ianaTzSchema,
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    working_start: hhmmSchema,
    working_end: hhmmSchema,
    status: z.string().max(140),
    default_visibility: visibilityLevelSchema,
  })
  .partial();
export type ProfileUpdate = z.infer<typeof profileUpdateSchema>;

export const interestsSchema = z
  .array(z.string().min(1).max(40))
  .max(20)
  .transform((tags) =>
    Array.from(new Set(tags.map((t) => t.trim().toLowerCase()))).filter(Boolean),
  );

export const travelEntryCreateSchema = z
  .object({
    city: z.string().min(1).max(120),
    country: z.string().min(1).max(120),
    country_code: z.string().length(2),
    timezone: ianaTzSchema,
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    date_from: isoDateSchema,
    date_to: isoDateSchema.nullish(),
    entry_type: entryTypeSchema,
    notes: z.string().max(2000).nullish(),
  })
  .refine((e) => e.date_to == null || e.date_from <= e.date_to, {
    message: "date_from must be on or before date_to",
    path: ["date_to"],
  });
export type TravelEntryCreate = z.infer<typeof travelEntryCreateSchema>;

export const travelEntryUpdateSchema = travelEntryCreateSchema._def.schema
  .partial()
  .refine((e) => e.date_to == null || e.date_from == null || e.date_from <= e.date_to, {
    message: "date_from must be on or before date_to",
    path: ["date_to"],
  });
export type TravelEntryUpdate = z.infer<typeof travelEntryUpdateSchema>;

export const citySearchQuerySchema = z.object({
  q: z.string().min(1).max(80),
  country: z.string().length(2).optional(),
});
